import { Button, IPublicRestaurant, Label, ReservationStatus, ReservationUtils, TIME_FORMAT } from '@localina/core';
import { ArrowLeftIcon, CalendarIcon, DoneIcon } from '@localina/icons';
import { formatNumber } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ISummaryRow, Page, SummaryCard } from '../../components';
import { useApp } from '../../contexts';
import { IApplicationStore, IBaseReservation } from '../../interfaces';
import * as actions from '../../store/cancelReservation/cancelReservation-actions';

interface IStoreProps {
    restaurant?: IPublicRestaurant;
    isLoading: boolean;
    reservation?: IBaseReservation;
    error?: { status: number; message: string };
}

interface IActionProps {
    cancelReservation: typeof actions.cancelReservation;
}

type TStep = 'overview' | 'confirm' | 'kept';

const STROKE_ICON_PROPS = {
    width: 17,
    height: 17,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
} as const;

const CheckGlyph = () => (
    <svg {...STROKE_ICON_PROPS} strokeWidth={2.2}>
        <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
);

const WarningGlyph = () => (
    <svg {...STROKE_ICON_PROPS} strokeWidth={1.8}>
        <path d="M12 4.2 21 19.4H3Z" />
        <path d="M12 10v4.1M12 16.6v.2" />
    </svg>
);

// "Mittwoch, 7. Oktober" / "Wednesday 7 October" / "mercredi 7 octobre" and the time as each language writes it
const DEADLINE_FORMATS: Record<string, { date: string; time: string }> = {
    de: { date: 'cccc, d. LLLL', time: TIME_FORMAT },
    fr: { date: 'cccc d LLLL', time: "HH'h'mm" },
};
const DEFAULT_DEADLINE_FORMAT = { date: 'cccc d LLLL', time: TIME_FORMAT };

// handset; the icon set only has a contact card and a mobile
const PhoneGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={18} height={18} strokeWidth={1.8}>
        <path d="M6.5 3.8h3.2l1.6 4-2 1.4a11.5 11.5 0 0 0 5.5 5.5l1.4-2 4 1.6v3.2a1.8 1.8 0 0 1-2 1.8A15.8 15.8 0 0 1 4.7 5.8a1.8 1.8 0 0 1 1.8-2Z" />
    </svg>
);

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

interface IBannerProps {
    type: 'ok' | 'warn' | 'crit';
    title?: string;
    children: React.ReactNode;
}

const Banner: React.FC<IBannerProps> = (props) => (
    <div className={`cancel-reservation__banner ${props.type}`} role={props.type === 'ok' ? 'status' : 'alert'}>
        <span className="cancel-reservation__banner__icon">
            {props.type === 'ok' ? <CheckGlyph /> : <WarningGlyph />}
        </span>
        <span>
            {props.title && <b>{props.title}</b>}
            {props.children}
        </span>
    </div>
);

function CancelReservation(props: IStoreProps & IActionProps) {
    const { t, i18n } = useTranslation();
    const { restaurantId, token } = useApp();
    const [step, setStep] = React.useState<TStep>('overview');

    const { reservation, restaurant } = props;
    if (!reservation || !restaurant) {
        return <Page name="cancel-reservation" isLoading={props.isLoading} restaurant={restaurant} />;
    }

    const locale = i18n.language === 'en' ? 'en-GB' : i18n.language;
    const reservationDateTime = DateTime.fromISO(reservation.reservationDateTime).setLocale(locale);
    const deadline = reservationDateTime.minus({ hour: restaurant.cancelReservationNotice || 0 });
    const deadlineFormat = DEADLINE_FORMATS[i18n.language] ?? DEFAULT_DEADLINE_FORMAT;
    const deadlineValues = {
        date: deadline.toFormat(deadlineFormat.date),
        time: deadline.toFormat(deadlineFormat.time),
    };

    const isCancelled = reservation.status === ReservationStatus.GUEST_CANCELLED;
    const isClosed = ReservationUtils.isReservationInDeadStatus(reservation);
    const isPastDeadline = deadline.diffNow().toMillis() < 0;

    const getClosedLabel = (status: ReservationStatus) => {
        switch (status) {
            case ReservationStatus.RESTAURANT_CANCELLED:
                return t('reservation.cancel.reservationCanceledByRestaurant');
            case ReservationStatus.NO_SHOW:
                return t('reservation.cancel.reservationNoShow');
            case ReservationStatus.DECLINED:
                return t('reservation.cancel.reservationDeclined');
        }
        return '';
    };

    const handleCancel = () => {
        if (!token) {
            return;
        }
        props.cancelReservation(restaurantId, token);
    };

    // the booking start of the same widget: everything but the cancellation token stays in the URL
    const handleNewReservation = () => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('token');
        window.location.assign(`${window.location.pathname}?${searchParams.toString()}`);
    };

    const callButton = (secondary: boolean) =>
        restaurant.phoneNumber ? (
            <Button
                label={t('reservation.cancel.buttons.call')}
                secondary={secondary}
                href={`tel:${restaurant.phoneNumber}`}
                startIcon={<PhoneGlyph />}
            />
        ) : null;

    const facts = (
        <dl className={`cancel-reservation__facts${isClosed ? ' struck' : ''}`}>
            <div>
                <dt>{t('reservation.cancel.fields.date')}</dt>
                <dd>{capitalize(reservationDateTime.toFormat('ccc dd.MM.yyyy'))}</dd>
            </div>
            <div>
                <dt>{t('reservation.cancel.fields.time')}</dt>
                <dd>{reservationDateTime.toFormat(TIME_FORMAT)}</dd>
            </div>
            <div>
                <dt>{t('reservation.cancel.fields.guests')}</dt>
                <dd>{t('reservation.cancel.guests', { count: reservation.participants })}</dd>
            </div>
        </dl>
    );

    const detailRows: ISummaryRow[] = [
        { key: 'name', icon: null, label: t('reservation.cancel.fields.name'), value: reservation.name },
        { key: 'email', icon: null, label: t('reservation.cancel.fields.email'), value: reservation.email },
    ];
    if (reservation.phoneNumber) {
        detailRows.push({
            key: 'phone',
            icon: null,
            label: t('reservation.cancel.fields.phone'),
            value: formatNumber(reservation.phoneNumber, 'INTERNATIONAL') || reservation.phoneNumber,
        });
    }
    const details = (
        <div className="cancel-reservation__details">
            <div className="widget-field__label">{t('reservation.cancel.details')}</div>
            <SummaryCard className="plain" rows={detailRows} />
        </div>
    );

    const heading = (title: string, lead?: string) => (
        <div className="widget-heading">
            <Label type="title" variant="h5" value={title} />
            {lead && <p className="widget-lead">{lead}</p>}
        </div>
    );

    // each screen's button row is keyed, so a button never fades over from the previous screen's colours
    const renderContent = () => {
        if (isCancelled) {
            return (
                <>
                    <div className="widget-done-status">
                        <span className="widget-done-status__icon approved">
                            <DoneIcon />
                        </span>
                        <Label
                            type="title"
                            value={t('reservation.cancel.cancelled.title')}
                            variant="h5"
                            align="center"
                        />
                        <p className="widget-lead">
                            {t('reservation.cancel.cancelled.lead', { email: reservation.email })}
                        </p>
                    </div>
                    {facts}
                    {details}
                    <div className="grow" />
                    <div key="cancelled" className="buttons">
                        <Button
                            label={t('reservation.cancel.buttons.newReservation')}
                            onClick={handleNewReservation}
                            startIcon={<CalendarIcon />}
                        />
                        {callButton(true)}
                    </div>
                </>
            );
        }

        if (isClosed) {
            return (
                <>
                    {heading(t('reservation.cancel.overview.title'))}
                    <Banner type="warn">{getClosedLabel(reservation.status)}</Banner>
                    {facts}
                    {details}
                    <div className="grow" />
                    {restaurant.phoneNumber && (
                        <div key="closed" className="buttons">
                            {callButton(false)}
                        </div>
                    )}
                </>
            );
        }

        if (step === 'confirm') {
            return (
                <>
                    {heading(t('reservation.cancel.confirm.title'), t('reservation.cancel.confirm.lead'))}
                    {facts}
                    <Banner type="crit">{t('reservation.cancel.confirm.warning')}</Banner>
                    <div className="grow" />
                    <div key="confirm" className="buttons">
                        <Button
                            label={t('common.buttons.back')}
                            secondary
                            onClick={() => setStep('overview')}
                            startIcon={<ArrowLeftIcon />}
                        />
                        <Button label={t('reservation.cancel.buttons.confirm')} onClick={handleCancel} />
                    </div>
                </>
            );
        }

        const keptNote = <Banner type="ok">{t('reservation.cancel.kept')}</Banner>;

        if (isPastDeadline) {
            return (
                <>
                    {heading(t('reservation.cancel.overview.title'), t('reservation.cancel.expired.lead'))}
                    {facts}
                    <Banner type="warn" title={t('reservation.cancel.expired.title')}>
                        {t('reservation.cancel.expired.text', deadlineValues)}
                    </Banner>
                    {details}
                    <div className="grow" />
                    {step === 'kept' ? (
                        keptNote
                    ) : (
                        <div key="expired" className="buttons">
                            <Button
                                label={t('reservation.cancel.buttons.keep')}
                                secondary
                                onClick={() => setStep('kept')}
                            />
                            {callButton(false)}
                        </div>
                    )}
                </>
            );
        }

        return (
            <>
                {heading(t('reservation.cancel.overview.title'), t('reservation.cancel.overview.lead'))}
                {facts}
                <Banner type="ok" title={t('reservation.cancel.free.title')}>
                    {t('reservation.cancel.free.text', deadlineValues)}
                </Banner>
                {details}
                <div className="grow" />
                {step === 'kept' ? (
                    keptNote
                ) : (
                    <div key="overview" className="buttons">
                        <Button
                            label={t('reservation.cancel.buttons.keep')}
                            secondary
                            onClick={() => setStep('kept')}
                        />
                        <Button label={t('reservation.cancel.buttons.cancel')} onClick={() => setStep('confirm')} />
                    </div>
                )}
            </>
        );
    };

    return (
        <Page name="cancel-reservation" isLoading={props.isLoading} restaurant={restaurant}>
            {renderContent()}
        </Page>
    );
}

const mapStateToProps = (state: IApplicationStore): IStoreProps => ({
    restaurant: state.restaurant.configuration,
    isLoading: state.cancelReservation.loading || state.restaurant.isLoading,
    reservation: state.cancelReservation.reservation,
    error: state.cancelReservation.error,
});

const actionProps: IActionProps = {
    cancelReservation: actions.cancelReservation,
};

export default connect(mapStateToProps, actionProps)(CancelReservation);
